import { useEffect, useState } from 'react';
import { useListContext, useTranslate } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import { Chart, useChart } from 'src/components/chart';
import { baseUrl, httpClient } from '../../../providers/AppDataProvider';
import { fNumber, fShortenNumber } from 'src/utils/format-number';

type CategoryStatItem = {
  category: string;
  totalVolumeUsdc: string;
  tradeCount: number;
  pct: number;
};

type CategoryStatsResponse = {
  categories: CategoryStatItem[];
  totalVolumeUsdc: string;
  totalTrades: number;
  categoryCount: number;
  topCategory: string;
  generatedAt?: number;
};

const CHART_COLORS = [
  '#3366FF',
  '#FF6B9D',
  '#FF3030',
  '#FF8C00',
  '#FFAB00',
  '#00B8D9',
  '#36B37E',
  '#6554C0',
  '#454F5B',
];

function StatCell({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 2,
        px: 1,
        flex: 1,
        bgcolor: 'background.neutral',
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          display: 'block',
          mb: 0.75,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} color={color ?? 'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
}

const CATEGORY_KEYS: Record<string, string> = {
  Crypto: 'crypto',
  Earnings: 'earnings',
  Economy: 'economy',
  Elections: 'elections',
  'Fed Rates': 'fed-rates',
  Finance: 'finance',
  Geopolitics: 'geopolitics',
  Macro: 'macro',
  Politics: 'politics',
  Culture: 'pop-culture',
  Primaries: 'primaries',
  Sports: 'sports',
  Tech: 'tech',
  Trump: 'trump',
  World: 'world',
  Other: 'other',
};

export const PredictionCategoryStatsChart = () => {
  const theme = useTheme();
  const translate = useTranslate();
  const { filterValues } = useListContext();
  const [data, setData] = useState<CategoryStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const isPrediction = filterValues?.tradingType === 'Predict';

  useEffect(() => {
    if (!isPrediction) return;

    let active = true;
    setLoading(true);

    const toUnixSeconds = (value?: string) => {
      if (!value) return undefined;
      const ms = new Date(value).getTime();
      return Number.isNaN(ms) ? undefined : Math.floor(ms / 1000);
    };

    const from = toUnixSeconds(filterValues?.fromTime);
    const to = toUnixSeconds(filterValues?.toTime);
    const params = new URLSearchParams();
    if (from != null) params.set('from', String(from));
    if (to != null) params.set('to', String(to));

    httpClient(
      `${baseUrl}/prediction/admin-builder-trade-stats/category-stats?${params.toString()}`,
      {
        method: 'GET',
      }
    )
      .then(({ json }) => {
        if (!active) return;
        setData(json ?? null);
      })
      .catch(() => {
        if (active) setData(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isPrediction, filterValues?.fromTime, filterValues?.toTime]);

  const categories = data?.categories ?? [];
  const totalVolume = Number(data?.totalVolumeUsdc ?? 0);
  const totalTrades = data?.totalTrades ?? 0;
  const categoryCount = data?.categoryCount ?? categories.length;
  const topCategory = data?.topCategory
    ? translate(
        `module.predictionDashboard.cat.${CATEGORY_KEYS[data.topCategory] ?? data.topCategory}`,
        { _: data.topCategory }
      )
    : '--';

  const chartSeries = categories.map((item) => Number(item.totalVolumeUsdc ?? 0));
  const chartLabels = categories.map((item) => {
    const key = CATEGORY_KEYS[item.category] ?? item.category;
    return translate(`module.predictionDashboard.cat.${key}`, { _: item.category });
  });

  const chartOptions = useChart({
    chart: { sparkline: { enabled: true } },
    colors: CHART_COLORS,
    labels: chartLabels,
    stroke: { width: 0 },
    tooltip: {
      custom: ({ seriesIndex }: { seriesIndex: number }) => {
        const item = categories[seriesIndex];
        if (!item) return '';
        const color = CHART_COLORS[seriesIndex % CHART_COLORS.length];
        const volume = `$${fShortenNumber(Number(item.totalVolumeUsdc))}`;
        const pct = item.pct >= 0.1 ? `${item.pct.toFixed(1)}%` : '<0.1%';
        const trades = Number(item.tradeCount).toLocaleString();
        return [
          `<div style="padding:10px 14px;background:#1c252e;border:1px solid rgba(255,255,255,0.1);border-radius:8px;min-width:220px">`,
          `<div style="font-weight:700;font-size:13px;color:#fff;margin-bottom:6px">${translate(
            `module.predictionDashboard.cat.${CATEGORY_KEYS[item.category] ?? item.category}`,
            {
              _: item.category,
            }
          )}</div>`,
          `<div style="display:flex;align-items:center;gap:8px;font-size:12px;color:#ccc">`,
          `<span style="width:10px;height:10px;border-radius:2px;background:${color};display:inline-block;flex-shrink:0"></span>`,
          `<span>${translate(
            `module.predictionDashboard.cat.${CATEGORY_KEYS[item.category] ?? item.category}`,
            {
              _: item.category,
            }
          )}: ${volume} (${pct}) — ${trades} trades</span>`,
          `</div></div>`,
        ].join('');
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '40%',
          labels: {
            value: {
              formatter: (value: number | string) => `$${fShortenNumber(value)}`,
            },
            total: {
              label: translate('module.predictionDashboard.total'),
              formatter: () => `$${fShortenNumber(totalVolume)}`,
            },
          },
        },
      },
    },
  });

  if (!isPrediction) return null;

  if (loading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={translate('module.predictionDashboard.categoryDistribution')}
          subheader={translate('module.predictionDashboard.volumeByCategory')}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 15,
            px: 2,
            py: 2,
          }}
        >
          <Skeleton variant="circular" width={400} height={400} sx={{ flexShrink: 0 }} />
          <Skeleton width={400} height={220} sx={{ flexShrink: 0 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, px: 2, pb: 2 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} height={86} sx={{ flex: 1, borderRadius: 1 }} />
          ))}
        </Box>
      </Card>
    );
  }

  if (categories.length === 0) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={translate('module.predictionDashboard.categoryDistribution')}
          subheader={translate('module.predictionDashboard.volumeByCategory')}
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            {translate('module.predictionDashboard.noData')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={translate('module.predictionDashboard.categoryDistribution')}
        subheader={translate('module.predictionDashboard.volumeByCategory')}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chart
          type="donut"
          series={chartSeries}
          options={chartOptions}
          height={400}
          width="50%"
          sx={{ mt: 2, mb: 1, flexShrink: 0 }}
        />

        <Box sx={{ px: 2, py: 2, alignSelf: 'center', minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pb: 0.75,
              mb: 0.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ width: 150 }}>
              <Typography
                variant="caption"
                color="text.disabled"
                fontWeight={700}
                textTransform="uppercase"
                letterSpacing={0.8}
              >
                {translate('module.predictionDashboard.categoryColumn')}
              </Typography>
            </Box>
            <Box sx={{ width: 100 }}>
              <Typography
                variant="caption"
                color="text.disabled"
                fontWeight={700}
                textTransform="uppercase"
                letterSpacing={0.8}
              >
                {translate('module.predictionDashboard.volumeColumn')}
              </Typography>
            </Box>
            <Box sx={{ width: 100, textAlign: 'right' }}>
              <Typography
                variant="caption"
                color="text.disabled"
                fontWeight={700}
                textTransform="uppercase"
                letterSpacing={0.8}
              >
                {translate('module.predictionDashboard.tradesColumn')}
              </Typography>
            </Box>
          </Box>

          {categories.map((item, index) => (
            <Box
              key={item.category}
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 0.75,
              }}
            >
              <Box sx={{ width: 150, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: CHART_COLORS[index % CHART_COLORS.length],
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" noWrap>
                  {translate(
                    `module.predictionDashboard.cat.${CATEGORY_KEYS[item.category] ?? item.category}`,
                    {
                      _: item.category,
                    }
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 100,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  ${fShortenNumber(Number(item.totalVolumeUsdc))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({item.pct >= 0.1 ? `${item.pct.toFixed(1)}%` : '<0.1%'})
                </Typography>
              </Box>
              <Box sx={{ width: 100, textAlign: 'right', flexShrink: 0 }}>
                <Typography variant="body2">{Number(item.tradeCount).toLocaleString()}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, px: 2, pb: 2, pt: 2 }}>
        <StatCell
          label={translate('module.predictionDashboard.totalVolume')}
          value={`$${fShortenNumber(totalVolume)}`}
        />
        <StatCell
          label={translate('module.predictionDashboard.totalTrades')}
          value={fNumber(totalTrades, { maximumFractionDigits: 0 })}
          color={theme.palette.warning.main}
        />
        <StatCell
          label={translate('module.predictionDashboard.categories')}
          value={String(categoryCount)}
          color={theme.palette.warning.main}
        />
        <StatCell
          label={translate('module.predictionDashboard.topCategory')}
          value={topCategory}
          color={theme.palette.success.main}
        />
      </Box>
    </Card>
  );
};
